import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MessageCircle, X, Send, Loader2, ShoppingBag, LifeBuoy, ChevronLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/support`;
const KEY = "venka_chat";

const TX = {
  en: {
    open: "Chat with us", title: "VENKA World Express", online: "We usually answer within minutes during office hours",
    hello: "Hello! How can we help you today?", sales: "Sales", salesSub: "Prices, packages, a new application",
    support: "Support", supportSub: "Help with my case, documents or payment", name: "Your name", email: "Email",
    phone: "Phone (optional)", message: "Your message", start: "Start the conversation", placeholder: "Write a message…",
    you: "You", newChat: "New conversation", privacy: "We use your details only to answer you.", error: "Could not send. Please try again.",
  },
  pt: {
    open: "Fale connosco", title: "VENKA World Express", online: "Respondemos normalmente em minutos no horário de expediente",
    hello: "Olá! Como podemos ajudar hoje?", sales: "Comercial", salesSub: "Preços, pacotes, um novo pedido",
    support: "Apoio ao cliente", supportSub: "Ajuda com o meu processo, documentos ou pagamento", name: "O seu nome", email: "Email",
    phone: "Telefone (opcional)", message: "A sua mensagem", start: "Iniciar conversa", placeholder: "Escreva uma mensagem…",
    you: "Você", newChat: "Nova conversa", privacy: "Usamos os seus dados apenas para lhe responder.", error: "Não foi possível enviar. Tente novamente.",
  },
  fr: {
    open: "Discutez avec nous", title: "VENKA World Express", online: "Nous répondons en général en quelques minutes aux heures de bureau",
    hello: "Bonjour ! Comment pouvons-nous vous aider ?", sales: "Commercial", salesSub: "Prix, forfaits, une nouvelle demande",
    support: "Assistance", supportSub: "Aide sur mon dossier, mes documents ou un paiement", name: "Votre nom", email: "Email",
    phone: "Téléphone (facultatif)", message: "Votre message", start: "Démarrer la conversation", placeholder: "Écrivez un message…",
    you: "Vous", newChat: "Nouvelle conversation", privacy: "Vos données servent uniquement à vous répondre.", error: "Envoi impossible. Réessayez.",
  },
};

const HIDDEN = [/^\/portal/, /^\/magic/, /^\/activate/];

const store = {
  get: () => { try { return JSON.parse(localStorage.getItem(KEY) || "null"); } catch (e) { return null; } },
  set: (v) => { try { if (v) localStorage.setItem(KEY, JSON.stringify(v)); else localStorage.removeItem(KEY); } catch (e) { /* private mode */ } },
};

const time = (iso) => { try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); } catch (e) { return ""; } };

/** Floating chat bubble on the public website and the customer area; conversations land in ERP > Support. */
export const ChatWidget = () => {
  const { i18n } = useTranslation();
  const lang = ["en", "pt", "fr"].includes((i18n.language || "").slice(0, 2)) ? i18n.language.slice(0, 2) : "en";
  const tx = TX[lang];
  const location = useLocation();
  const { user } = useAuth();
  const [enabled, setEnabled] = useState(true);
  const [open, setOpen] = useState(false);
  const [conv, setConv] = useState(() => store.get());
  const [msgs, setMsgs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [dept, setDept] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const endRef = useRef(null);
  const lastRef = useRef("");
  const openRef = useRef(open);
  openRef.current = open;

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/settings/public`).then((r) => r.ok && r.json())
      .then((s) => { if (s && s.chat && s.chat.enabled === false) setEnabled(false); }).catch(() => {});
  }, []);
  useEffect(() => { if (new URLSearchParams(location.search).get("chat")) setOpen(true); }, [location.search]);
  useEffect(() => {
    if (user && user.role === "customer") setForm((f) => ({ ...f, name: f.name || user.name || "", email: f.email || user.email || "", phone: f.phone || user.phone || "" }));
  }, [user]);

  const poll = useCallback(async () => {
    if (!conv) return;
    try {
      const qs = lastRef.current ? `?since=${encodeURIComponent(lastRef.current)}` : "";
      const r = await fetch(`${API}/chat/${conv.id}${qs}`, { headers: { "X-Chat-Token": conv.token } });
      if (r.status === 404) { store.set(null); setConv(null); setMsgs([]); lastRef.current = ""; return; }
      if (!r.ok) return;
      const d = await r.json();
      if (d.messages.length) {
        const initial = !lastRef.current;
        lastRef.current = d.messages[d.messages.length - 1].created_at;
        setMsgs((cur) => {
          const seen = new Set(cur.map((m) => m.id));
          const add = d.messages.filter((m) => !seen.has(m.id));
          if (!openRef.current && !initial) setUnread((u) => u + add.filter((m) => m.from === "staff").length);
          return [...cur, ...add];
        });
      }
    } catch (e) { /* offline */ }
  }, [conv]);

  useEffect(() => {
    if (!conv) return undefined;
    poll();
    const iv = setInterval(poll, open ? 4000 : 30000);
    return () => clearInterval(iv);
  }, [conv, open, poll]);
  useEffect(() => { if (open) { setUnread(0); setTimeout(() => endRef.current?.scrollIntoView({ block: "end" }), 50); } }, [open, msgs.length]);

  if (!enabled || HIDDEN.some((rx) => rx.test(location.pathname))) return null;

  const authHeader = () => {
    try { const t = localStorage.getItem("venka_token"); return t ? { Authorization: `Bearer ${t}` } : {}; } catch (e) { return {}; }
  };

  const start = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const r = await fetch(`${API}/chat/start`, {
        method: "POST", headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({ ...form, department: dept, lang, page: location.pathname }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setError(typeof d.detail === "string" ? d.detail : tx.error); return; }
      const c = { id: d.id, token: d.token };
      store.set(c); lastRef.current = ""; setMsgs([]); setConv(c);
      setForm((f) => ({ ...f, message: "" }));
    } catch (err) { setError(tx.error); } finally { setBusy(false); }
  };

  const send = async (e) => {
    e?.preventDefault();
    const body = text.trim();
    if (!body || !conv) return;
    setBusy(true); setError("");
    try {
      const r = await fetch(`${API}/chat/${conv.id}/messages`, {
        method: "POST", headers: { "Content-Type": "application/json", "X-Chat-Token": conv.token }, body: JSON.stringify({ body }),
      });
      if (!r.ok) { setError(tx.error); return; }
      setText("");
      await poll();
    } catch (err) { setError(tx.error); } finally { setBusy(false); }
  };

  const reset = () => { store.set(null); setConv(null); setMsgs([]); setDept(null); lastRef.current = ""; };
  const input = "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#2542E2] focus:outline-none focus:ring-2 focus:ring-[#2542E2]/20";

  return (
    <div className="fixed bottom-4 right-4 z-[60] flex flex-col items-end gap-3 font-sans sm:bottom-6 sm:right-6" data-testid="chat-widget">
      {open && (
        <div className="flex h-[min(620px,calc(100vh-7rem))] w-[calc(100vw-2rem)] max-w-[380px] flex-col overflow-hidden rounded-3xl bg-white shadow-2xl ring-1 ring-black/5" data-testid="chat-panel">
          <div className="relative bg-gradient-to-br from-[#0A0F1F] via-[#12205e] to-[#2542E2] px-5 pb-5 pt-4 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {conv && <button type="button" onClick={reset} className="-ml-1 rounded-full p-1 hover:bg-white/10" title={tx.newChat} aria-label={tx.newChat}><ChevronLeft className="h-4 w-4" /></button>}
                <img src="/venka-logo.png" alt="" className="h-8 w-8 rounded-full bg-white object-contain p-0.5" />
                <div>
                  <p className="text-sm font-semibold leading-tight">{tx.title}</p>
                  <p className="flex items-center gap-1.5 text-[11px] text-white/70"><span className="h-1.5 w-1.5 rounded-full bg-[#4FC42C]" />{tx.online}</p>
                </div>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="rounded-full p-1.5 hover:bg-white/10" aria-label="Close" data-testid="chat-close"><X className="h-4 w-4" /></button>
            </div>
            {!conv && <p className="mt-4 text-lg font-semibold">{tx.hello}</p>}
          </div>

          {!conv && !dept && (
            <div className="flex-1 space-y-3 overflow-y-auto bg-slate-50 p-4">
              {[["sales", ShoppingBag, tx.sales, tx.salesSub], ["support", LifeBuoy, tx.support, tx.supportSub]].map(([k, Icon, title, sub]) => (
                <button key={k} type="button" onClick={() => setDept(k)} data-testid={`chat-dept-${k}`}
                  className="flex w-full items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:border-[#2542E2] hover:shadow">
                  <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${k === "sales" ? "bg-[#2542E2]/10 text-[#2542E2]" : "bg-[#4FC42C]/15 text-[#2f8a17]"}`}><Icon className="h-5 w-5" /></span>
                  <span><span className="block text-sm font-semibold text-slate-900">{title}</span><span className="block text-xs text-slate-500">{sub}</span></span>
                </button>
              ))}
            </div>
          )}

          {!conv && dept && (
            <form onSubmit={start} className="flex-1 space-y-2.5 overflow-y-auto bg-slate-50 p-4" data-testid="chat-start-form">
              <button type="button" onClick={() => setDept(null)} className="mb-1 inline-flex items-center gap-1 text-xs font-medium text-[#2542E2]"><ChevronLeft className="h-3.5 w-3.5" />{dept === "sales" ? tx.sales : tx.support}</button>
              <input className={input} required maxLength={120} placeholder={tx.name} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="chat-name" />
              <input className={input} required type="email" maxLength={200} placeholder={tx.email} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="chat-email" />
              <input className={input} maxLength={40} placeholder={tx.phone} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <textarea className={`${input} min-h-[96px] resize-none`} required maxLength={4000} placeholder={tx.message} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="chat-message" />
              {error && <p className="text-xs text-red-600">{error}</p>}
              <button type="submit" disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#2542E2] py-2.5 text-sm font-semibold text-white shadow hover:bg-[#1c34b8] disabled:opacity-60" data-testid="chat-start">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}{tx.start}
              </button>
              <p className="text-center text-[11px] text-slate-400">{tx.privacy}</p>
            </form>
          )}

          {conv && (
            <>
              <div className="flex-1 space-y-2.5 overflow-y-auto bg-slate-50 px-4 py-4" data-testid="chat-thread">
                {msgs.map((m) => m.from === "system" ? (
                  <p key={m.id} className="mx-auto max-w-[90%] rounded-xl bg-[#4FC42C]/10 px-3 py-2 text-center text-xs text-slate-600">{m.body}</p>
                ) : (
                  <div key={m.id} className={`flex ${m.from === "visitor" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[82%] rounded-2xl px-3.5 py-2 text-sm shadow-sm ${m.from === "visitor" ? "rounded-br-md bg-[#2542E2] text-white" : "rounded-bl-md bg-white text-slate-800 ring-1 ring-slate-200"}`}>
                      {m.from === "staff" && <p className="mb-0.5 text-[11px] font-semibold text-[#2542E2]">{m.author_name}</p>}
                      <p className="whitespace-pre-wrap break-words">{m.body}</p>
                      <p className={`mt-0.5 text-right text-[10px] ${m.from === "visitor" ? "text-white/60" : "text-slate-400"}`}>{time(m.created_at)}</p>
                    </div>
                  </div>
                ))}
                <div ref={endRef} />
              </div>
              <form onSubmit={send} className="flex items-end gap-2 border-t border-slate-200 bg-white p-3">
                <textarea rows={1} className={`${input} max-h-28 resize-none`} placeholder={tx.placeholder} value={text} maxLength={4000}
                  onChange={(e) => setText(e.target.value)} data-testid="chat-input"
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} />
                <button type="submit" disabled={busy || !text.trim()} className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#2542E2] text-white disabled:opacity-50" aria-label="Send" data-testid="chat-send">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </button>
              </form>
              {error && <p className="bg-white px-4 pb-2 text-xs text-red-600">{error}</p>}
            </>
          )}
        </div>
      )}

      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="chat-bubble" aria-label={tx.open}
        className="group relative flex h-14 items-center gap-2 rounded-full bg-[#2542E2] pl-4 pr-5 text-white shadow-xl shadow-[#2542E2]/30 transition hover:bg-[#1c34b8]">
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
        <span className="hidden text-sm font-semibold sm:inline">{tx.open}</span>
        {unread > 0 && !open && <span className="absolute -right-1 -top-1 grid h-5 min-w-[1.25rem] place-items-center rounded-full bg-[#4FC42C] px-1 text-[11px] font-bold text-white ring-2 ring-white" data-testid="chat-unread">{unread}</span>}
      </button>
    </div>
  );
};

export default ChatWidget;
